import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../../../components/ui/dialog';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../components/ui/select';
import { Switch } from '../../../../../components/ui/switch';
import { Textarea } from '../../../../../components/ui/textarea';
import type { CreateWorkflowStatusInput, StatusType, WorkflowType } from '@leal-crm/shared';

interface StatusCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workflowType: WorkflowType;
  onCreate: (data: CreateWorkflowStatusInput) => void;
  isSaving: boolean;
}

export function StatusCreateDialog({ open, onOpenChange, workflowType, onCreate, isSaving }: StatusCreateDialogProps) {
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    statusType: 'regular' as StatusType,
    isInitial: false,
    customerLabel: '',
    isVisibleToCustomer: true,
    showInJobboard: true,
  });

  useEffect(() => {
    if (!open) {
      setFormData({
        code: '',
        name: '',
        description: '',
        statusType: 'regular',
        isInitial: false,
        customerLabel: '',
        isVisibleToCustomer: true,
        showInJobboard: true,
      });
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nuevo estado</DialogTitle>
          <DialogDescription>Agrega un estado al flujo actual</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="code">Código</Label>
              <Input
                id="code"
                value={formData.code}
                onChange={(e) => setFormData((f) => ({ ...f, code: e.target.value }))}
                placeholder="PENDING_CONFIRMATION"
              />
              <p className="text-xs text-muted-foreground">Usa MAYUS y guiones bajos.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">Nombre</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData((f) => ({ ...f, name: e.target.value }))}
                placeholder="Pendiente de confirmación"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Descripción</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData((f) => ({ ...f, description: e.target.value }))}
              placeholder="Descripción opcional"
              rows={2}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Tipo de estado</Label>
              <Select
                value={formData.statusType}
                onValueChange={(value) => setFormData((f) => ({ ...f, statusType: value as StatusType }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="start">Inicio</SelectItem>
                  <SelectItem value="regular">Regular</SelectItem>
                  <SelectItem value="terminal">Terminal</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Inicial</Label>
              <div className="flex items-center justify-between rounded-lg border px-3 py-2">
                <span className="text-sm text-muted-foreground">Estado inicial del flujo</span>
                <Switch
                  checked={formData.isInitial}
                  onCheckedChange={(checked) => setFormData((f) => ({ ...f, isInitial: checked }))}
                />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="customerLabel">Etiqueta para cliente</Label>
            <Input
              id="customerLabel"
              value={formData.customerLabel}
              onChange={(e) => setFormData((f) => ({ ...f, customerLabel: e.target.value }))}
              placeholder="Texto que ve el cliente (opcional)"
            />
          </div>

          <div className="space-y-3 border-t pt-4">
            {workflowType === 'booking' ? (
              <div className="flex items-center justify-between">
                <div>
                  <Label>Visible para cliente</Label>
                  <p className="text-xs text-muted-foreground">El cliente puede ver este estado</p>
                </div>
                <Switch
                  checked={formData.isVisibleToCustomer}
                  onCheckedChange={(checked) => setFormData((f) => ({ ...f, isVisibleToCustomer: checked }))}
                />
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div>
                  <Label>Mostrar en tablero</Label>
                  <p className="text-xs text-muted-foreground">Aparece como columna en el tablero</p>
                </div>
                <Switch
                  checked={formData.showInJobboard}
                  onCheckedChange={(checked) => setFormData((f) => ({ ...f, showInJobboard: checked }))}
                />
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancelar
          </Button>
          <Button
            onClick={() =>
              onCreate({
                code: formData.code.trim(),
                name: formData.name.trim(),
                description: formData.description.trim() || undefined,
                statusType: formData.statusType,
                isInitial: formData.isInitial,
                customerLabel: formData.customerLabel.trim() || undefined,
                isVisibleToCustomer: workflowType === 'booking' ? formData.isVisibleToCustomer : undefined,
                showInJobboard: workflowType === 'job' ? formData.showInJobboard : undefined,
              })
            }
            disabled={isSaving || !formData.code.trim() || !formData.name.trim()}
          >
            {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Crear estado
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
